import { TableV2 } from 'components/Table/TableV2';
import { useUserCalendar } from 'modules/calendar/hooks/useUserCalendar';
import { calendarOrganizationColumns } from 'modules/calendar/constants/calendarOrganizationColumns';
import { SearchInput } from 'common/ui';
import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Button } from 'system/atoms';
import { SxProps } from '@mui/material/styles';
import { Paths } from 'services/navigation/paths';
import { ToastType, useToast } from 'services/toast';
import { UserCalendarActions } from './UserCalendarActions';
import { usePostRest, useDeleteRest } from 'common/hooks';
import { ApiPaths } from 'modules/calendar/constants/apiPaths';
import { useConfirmationModal } from 'services/modals/hooks';
import { useLoadingOverlay } from 'services/loading/hooks';
import { FailedMessageWithHref } from 'common/ui';
import { UserCalendarStatus } from 'generated-graphql/types';
import { ConfirmationModalType } from 'services/modals/constants';
import { TextBlock } from 'common/ui/TextBlock';
import { CalendarMeetingTemplateList } from './CalendarMeetingTemplateList';
import { RouterLink } from 'common/ui/RouterLink';
import { Flags, useFlag } from 'services/flags';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: 2,
  my: '20px'
};

export const CalendarOrganizationList = () => {
  const { showToast } = useToast();
  const { showModal } = useConfirmationModal();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const areMeetingTemplatesHidden = Boolean(
    useFlag(Flags.HIDE_CLASSIC_MEETING_TEMPLATES)
  );

  const { mutateAsync: resendInviteMutate } = usePostRest<
    void,
    { email: string }
  >(ApiPaths.RESEND_INVITE);

  const { mutateAsync: deleteUserCalendarOrganization } = useDeleteRest<void>(
    ApiPaths.USER_CALENDAR_ORGANIZATION
  );

  const [search, setSearch] = useState('');
  const { loading, data, error, refetch } = useUserCalendar({
    search
  });

  useEffect(() => {
    if (!error) return;
    showToast({
      title: 'Failed to load contacts',
      message: (
        <FailedMessageWithHref
          failedMessage="Refresh the page to try again."
          emailSubject="Calendar contacts failed to load"
        />
      ),
      type: ToastType.Error
    });
  }, [error, showToast]);

  const handleRemoveUserCalendar = async ({
    userCalendarOrganizationId,
    successMessage,
    name
  }: {
    userCalendarOrganizationId: string;
    name: string;
    successMessage?: string;
  }) => {
    showLoadingOverlay();
    try {
      await deleteUserCalendarOrganization(userCalendarOrganizationId);
      await refetch();
      showToast({
        title: `${name} deleted`,
        message: successMessage,
        type: ToastType.Success
      });
    } catch (e: any) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try deleting this contact again."
            emailSubject="Deleting contact failed"
          />
        ),
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  const handleResendInvite = async (email: string) => {
    showLoadingOverlay();
    try {
      await resendInviteMutate({ email });
      showToast({
        title: 'Resent',
        message: 'Your invitation has been resent.',
        type: ToastType.Success
      });
    } catch (e) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try resending the invitation again."
            emailSubject="Resending invitation failed"
          />
        ),
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  const showResendInviteModal = (email: string) => {
    showModal({
      title: 'Resend invitation?',
      confirmationText: 'Resend invitation',
      cancelText: 'Cancel',
      onConfirm: () => handleResendInvite(email)
    });
  };

  const showRemoveUserCalendarModal = ({
    userCalendarOrganizationId,
    userCalendarStatus,
    name
  }: {
    userCalendarOrganizationId: string;
    name: string;
    userCalendarStatus: UserCalendarStatus;
  }) => {
    if (userCalendarStatus === UserCalendarStatus.Invited) {
      showModal({
        title: 'Are you sure?',
        message: 'The invitation sent will no longer be valid.',
        modalType: ConfirmationModalType.DESTRUCTIVE,
        destructiveText: 'Delete',
        cancelText: 'Cancel',
        onDestructiveConfirm: () =>
          handleRemoveUserCalendar({
            userCalendarOrganizationId,
            name,
            successMessage: 'The invitation sent will no longer be valid.'
          })
      });
    } else {
      showModal({
        title: 'Are you sure?',
        message:
          'They will be removed immediately from any scheduled meetings and meeting templates.',
        modalType: ConfirmationModalType.DESTRUCTIVE,
        destructiveText: 'Delete',
        cancelText: 'Cancel',
        onDestructiveConfirm: () =>
          handleRemoveUserCalendar({
            userCalendarOrganizationId,
            name
          })
      });
    }
  };

  const columns = useMemo(() => {
    const columns = [...calendarOrganizationColumns];

    columns.push({
      name: 'Actions',
      isAction: true,
      cell: (row) => (
        <UserCalendarActions
          userCalendar={row}
          onRemove={() =>
            showRemoveUserCalendarModal({
              userCalendarOrganizationId: row.userCalendarOrganizationId,
              name: row.name,
              userCalendarStatus: row.status
            })
          }
          onResendInvite={() => showResendInviteModal(row.email)}
        />
      )
    });

    return columns;
  }, [
    handleRemoveUserCalendar,
    showResendInviteModal,
    calendarOrganizationColumns
  ]);

  return (
    <>
      {!areMeetingTemplatesHidden && (
        <>
          <CalendarMeetingTemplateList showCreateButton={data.length !== 0} />
          <Box sx={{ mt: 10 }} />
        </>
      )}
      <TextBlock
        variant="section"
        title="Meeting panel"
        subtitle="Add and manage your meeting panel."
      />
      <Box sx={container}>
        <SearchInput
          sx={{ width: 320 }}
          placeholder="Search contact names"
          onChange={(e) => setSearch(e)}
        />
        <RouterLink to={Paths.CALENDAR_ADD_CONTACT}>
          <Button variant="primary">Add contact</Button>
        </RouterLink>
      </Box>
      <TableV2 columns={columns} isLoading={loading} data={data} pagination />
    </>
  );
};
